import React, { Fragment, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { IMAGES } from '../../../constants';
import { useSelector, useDispatch } from 'react-redux';
import Avatar from 'react-avatar';
import { useLocation } from 'react-router-dom';

import {
    convertLocaleTime,
    convertLocaleDate,
    scrollToBottomChat,
    scrollToBottomChatNew,
    convertLocaleMomentDate,
    chatCalendarDay,
    formatChatDayLabel,
    encryptData,
    decryptData
} from '../../../helper';
import Welcome from './Welcome';
import $ from 'jquery';
import { socket } from '../../../utils/socket';
import { useAlert } from 'react-alert';
import { FormattedMessage, useIntl } from 'react-intl';
import LightGallery from 'lightgallery/react';
import lgZoom from 'lightgallery/plugins/zoom';
import lgVideo from 'lightgallery/plugins/video';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import validator from 'validator';
import { url, IMAGE_BASE_URL } from '../../../config';
import {
    getChatUserAction,
    getMessageHistoryAction,
    getshortMsg,
    updateChatDisappearAction
} from '../../../actions/chatAction';
import Loader from '../../layout/Loader';
import ChatLoader from '../../layout/ChatLoader';
import DeleteMessageModal from './DeleteMessageModal';

const DELETE_FOR_EVERYONE_MS = 48 * 60 * 60 * 1000;
const DURATION_24H = 86400;
const DURATION_7D = 604800;

const canDeleteMessageForEveryone = (messageData, userId) => {
    if (!messageData || Number(messageData.user_id) !== Number(userId)) return false;
    if (!messageData.created_at) return false;
    const created = new Date(messageData.created_at.includes('T') ? messageData.created_at : messageData.created_at + 'Z');
    return (Date.now() - created.getTime()) <= DELETE_FOR_EVERYONE_MS;
};

// ============================================================================
// CUSTOM HOOKS FOR BETTER SEPARATION OF CONCERNS
// ============================================================================

const useUserSession = () => {
    const userSession = JSON.parse(sessionStorage.getItem('user') || '{}');
    return {
        user_id: userSession?.id,
        user_name: userSession?.name,
        profile_picture_full_thumb: userSession?.profile_picture_full_thumb
    };
};

const useChatSession = () => {
    return {
        other_user_id: sessionStorage.getItem('other_user_id'),
        conversationId: sessionStorage.getItem('socket_group_id'),
        other_user_name: sessionStorage.getItem('other_user_name'),
        other_profile_picture_full_thumb: sessionStorage.getItem('other_profile_picture_full_thumb')
    };
};

const useSocketConnection = () => {
    useEffect(() => {
        const interval = setInterval(() => {
            if (socket) {
                socket.connected = true;
            }
        }, 3000);

        return () => clearInterval(interval);
    }, []);
};


const processText = (text) => {
    const urlPattern = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
    const parts = text.split(urlPattern).map((part, index) => {
        if (urlPattern.test(part)) {
            return (
                <a
                    key={index}
                    href={part.startsWith('www') ? `http://${part}` : part}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {part}
                </a>
            );
        }
        return part;
    });
    return parts;
};

const escapeHtml = (value) => {
    if (value === null || value === undefined) return '';
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
};

const isJson = (str) => {
    try {
        JSON.parse(str);
        return true;
    } catch (e) {
        return false;
    }
};

// ============================================================================
// MEMOIZED COMPONENTS FOR BETTER PERFORMANCE
// ============================================================================

const MessageStatusIcon = React.memo(({ isRead, isDelivered }) => {
    if (isRead == 1) {
        return <i className="fa-solid fa-check-double text-right font-14 class-blue"></i>;
    } else if (isRead == 0 && isDelivered == 0) {
        return <i className="fa fa-check text-right font-14" aria-hidden="true"></i>;
    } else if (isRead == 0 && isDelivered == 1) {
        return <i className="fa-solid fa-check-double d-block text-right font-14"></i>;
    }
    return null;
});

const MessageAttachment = React.memo(({ attachment, messageType, onClick }) => {
    if (!attachment?.image_url) return null;

    const renderAttachment = () => {
        switch (messageType) {
            case 'image':
                return (
                    <LightGallery plugins={[lgThumbnail, lgZoom, lgVideo]} mode="lg-fade" download={false}>
                        <img
                            src={IMAGE_BASE_URL + attachment.image_url}
                            alt="Product Image"
                            style={{ borderRadius: "10px 0 10px 10px" }}
                            width={60}
                            height={60}
                            onClick={onClick}
                        />
                    </LightGallery>
                );
            case 'video':
                return (
                    <video width="320" height="240" controls>
                        <source src={IMAGE_BASE_URL + attachment.image_url} type="video/mp4" />
                        <source src={IMAGE_BASE_URL + attachment.image_url} type="video/ogg" />
                        Your browser does not support the video tag.
                    </video>
                );
            case 'pdf':
                return (
                    <a href={IMAGE_BASE_URL + attachment.image_url} target="_blank">
                        <i className="fa fa-file-pdf-o" style={{ fontSize: '48px', color: 'red' }}></i>
                    </a>
                );
            default:
                return null;
        }
    };

    return (
        <div className="s-i-image">
            {renderAttachment()}
            {attachment.file_content && <p>{processText(attachment.file_content)}</p>}
        </div>
    );
});

const MessageItem = React.memo(({
    messageData,
    user_id,
    handleImage,
    isOwnMessage,
    selectionMode,
    selected,
    onToggleSelect
}) => {
    const attachment = useMemo(() => {
        let att = messageData?.attachment_url;
        if (isJson(att)) {
            att = JSON.parse(att);
        }
        return att;
    }, [messageData?.attachment_url]);

    const messageClass = isOwnMessage ? 'u-messgae' : 'ownCmnt';
    const hasAttachment = messageData?.attachment_url && messageData?.attachment_url !== 'null';

    const handleProductClick = useCallback(() => {
        if (attachment?.product_url) {
            window.location.href = url + "/details/" + attachment.product_url;
        }
    }, [attachment?.product_url]);

    const onLongPressSelect = useCallback((e) => {
        e.preventDefault();
        if (onToggleSelect && messageData?.id) {
            onToggleSelect(messageData.id);
        }
    }, [onToggleSelect, messageData?.id]);

    return (
        <div
            className={`d-flex my-4 align-content-end ${messageClass} mesgs ${hasAttachment ? 'NewChatLink' : ''} ${selected ? 'msg-selected' : ''}`}
            data-chat-id={messageData?.id}
            onContextMenu={onLongPressSelect}
            onClick={(e) => {
                if (selectionMode && onToggleSelect && messageData?.id) {
                    e.preventDefault();
                    onToggleSelect(messageData.id);
                }
            }}
        >
            {selectionMode ? (
                <label className="msg-select-check" onClick={(e) => e.stopPropagation()}>
                    <input
                        type="checkbox"
                        checked={!!selected}
                        onChange={() => onToggleSelect && onToggleSelect(messageData?.id)}
                    />
                </label>
            ) : null}
            <div>
                <div
                    className="d-flex align-items-center txts"
                    onClick={handleProductClick}
                    style={{ cursor: attachment?.product_url ? 'pointer' : 'default' }}
                >
                    {attachment?.image_url && (
                        <div className="s-i-image" onClick={() => attachment?.product_url ? window.location.href = url + "/details/" + attachment?.product_url : {}}>
                            {messageData?.message_type == 'image' && (
                                <><LightGallery plugins={[lgThumbnail, lgZoom, lgVideo]} mode="lg-fade" download={false}>
                                    <img src={attachment?.image_url ? IMAGE_BASE_URL + attachment?.image_url : attachment} alt="Product Image" style={{ borderRadius: "10px 0 10px 10px", }} width={60} height={60} onClick={() => { handleImage(attachment?.image_url ? attachment?.image_url : attachment, "image"); }} />

                                </LightGallery><p>{processText(attachment?.file_content ? attachment?.file_content : attachment)}</p>
                                </>

                            )}
                            {messageData?.message_type == 'video' && (
                                <><video width="320" height="240" controls>
                                    <source src={attachment?.image_url ? IMAGE_BASE_URL + attachment?.image_url : attachment} type="video/mp4" style={{ borderRadius: "10px 0 10px 10px", }} width={60} height={60}></source>
                                    <source src={attachment?.image_url ? attachment?.image_url : attachment} type="video/ogg" style={{ borderRadius: "10px 0 10px 10px", }} width={60} height={60}></source>

                                    Your browser does not support the video tag.

                                </video><p>{processText(attachment?.file_content ? attachment?.file_content : attachment)}</p></>

                            )}
                            {messageData?.message_type == 'pdf' && (
                                <a href={attachment?.image_url ? IMAGE_BASE_URL + attachment?.image_url : attachment} target="_blank">
                                    <i className="fa fa-file-pdf-o" style={{ fontSize: '48px', color: 'red' }}></i>
                                </a>

                            )}

                        </div>
                    )}

                    <div className="chatlinktext">
                        {(messageData?.message_type == 'text' || messageData?.message_type == 'TEXT') ?
                            <>
                                <div className="d-flex s-i-image">
                                    {
                                        attachment?.image_url &&
                                        <LightGallery plugins={[lgThumbnail, lgZoom, lgVideo]} mode="lg-fade" download={false}  >
                                            <img src={attachment?.image_url ? IMAGE_BASE_URL + attachment?.image_url : ""} alt="Product Image" style={{ borderRadius: "10px 0 10px 10px" }} width={60} height={60} onClick={() => { handleImage(messageData?.attachment_url, 'image') }} />
                                        </LightGallery>
                                    }
                                    {attachment?.product_url ? <a href={url + "/details/" + attachment?.product_url}>{processText(messageData.message)}</a> : <p> {processText(messageData.message)}</p>}
                                </div>

                            </>
                            :
                            <div>
                                {messageData?.message_type == "ENQUIRY" || messageData?.message_type == null ?
                                    <a
                                        className="d-flex align-items-center txts"
                                        // onClick={() => messageData?.product_id ? window.location.href = url + "/details/diamonds/" + messageData?.product_id : {}}
                                        href={url + "/details/diamonds/" + messageData?.product_id}
                                        style={{ cursor: 'pointer' }}
                                        target='_blank'
                                    >
                                        {

                                            attachment?.image_url &&
                                            <div className="s-i-image">
                                                <LightGallery
                                                    plugins={[lgThumbnail, lgZoom, lgVideo]}
                                                    mode="lg-fade"
                                                    download={false}
                                                >
                                                    <img
                                                        src={attachment?.image_url ? IMAGE_BASE_URL + attachment?.image_url : attachment}
                                                        alt="Product Image"
                                                        style={{ borderRadius: "10px 0 10px 10px" }}
                                                        width={60} height={60}
                                                        onClick={() => { handleImage(messageData?.attachment_url, 'image') }} />
                                                </LightGallery>
                                            </div>
                                        }
                                        <div className="chatlinktext">
                                            <p>{processText(messageData?.message)}</p>
                                        </div>
                                    </a>
                                    : null}
                            </div>

                        }
                    </div>
                </div>
                <div className="d-flex dats">
                    <p>
                        {convertLocaleMomentDate(messageData?.created_at)}
                        {isOwnMessage && (
                            <MessageStatusIcon
                                isRead={messageData?.is_read}
                                isDelivered={messageData?.is_delivered}
                            />
                        )}
                    </p>
                </div>
            </div>
        </div>
    );
});

// ============================================================================
// MAIN CHATBOX COMPONENT
// ============================================================================

const Chatbox = (props) => {
    const alert = useAlert();
    const dispatch = useDispatch();
    const location = useLocation();
    const messageContainerRef = useRef(null);
    const lastChatDayRef = useRef('');
    const enquirySentRef = useRef(false);
    const chatMenuRef = useRef(null);

    // Custom hooks
    const { user_id, user_name, profile_picture_full_thumb } = useUserSession();
    const { other_user_id, conversationId, other_user_name, other_profile_picture_full_thumb } = useChatSession();

    // State management
    const [message, setMessage] = useState('');
    const [chatData, setChatData] = useState([]);
    const [hash, setHash] = useState('0');
    const [page, setPage] = useState(1);
    const [limit] = useState(20);
    const loadingOlderRef = useRef(false);
    const [productDetailsStatus, setProductDetailsStatus] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [selectionMode, setSelectionMode] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [disappearSeconds, setDisappearSeconds] = useState(0);
    const [showChatMenu, setShowChatMenu] = useState(false);

    // Redux selectors
    const { messageHistory, loading } = useSelector(state => state.messageHistory);
    const { chatuser } = useSelector(state => state.chatuser);
    const { shortmsg } = useSelector(state => state.shortmsg);
    const { active_status } = useSelector(state => state.ChatActiveState);

    // Socket connection management
    useSocketConnection();

    useEffect(() => {
        dispatch(getshortMsg());
    }, [dispatch]);

    // Close menu when switching chat threads
    useEffect(() => {
        setShowChatMenu(false);
        setSelectionMode(false);
        setSelectedIds([]);
        setShowDeleteModal(false);
        setDisappearSeconds(0);
    }, [other_user_id, conversationId, active_status]);

    // Close dropdown on outside click
    useEffect(() => {
        if (!showChatMenu) return undefined;

        const handleOutsideClick = (event) => {
            if (chatMenuRef.current && !chatMenuRef.current.contains(event.target)) {
                setShowChatMenu(false);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('touchstart', handleOutsideClick);

        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('touchstart', handleOutsideClick);
        };
    }, [showChatMenu]);

    useEffect(() => {
    if (!sessionStorage.getItem('other_user_id')) {
        return;
    }
    if (!messageHistory || !Array.isArray(messageHistory.data)) {
        return;
    }

    const openingThread = sessionStorage.getItem('newChat') === '0';
    let openTimer;
    let scrollTimer;
    if (openingThread) {
        setHash('0');
        setPage(1);
        setMessage('');
        sessionStorage.removeItem('newChat');
        openTimer = setTimeout(() => {
            setHash('1');
            scrollToBottomChatNew(messageContainerRef);
        }, 100);
    }

    const first = messageHistory.data[0];
    if (first && typeof first.disappear_seconds !== 'undefined') {
        setDisappearSeconds(Number(first.disappear_seconds) || 0);
    }

    const isFirstPage = !messageHistory.current_page || Number(messageHistory.current_page) <= 1;
    if (openingThread || isFirstPage) {
        setChatData((prev) => {
            if (openingThread || prev.length === 0) {
                return messageHistory.data;
            }
            return prev;
        });
        if (openingThread) {
            setSelectedIds([]);
            setSelectionMode(false);
            scrollTimer = setTimeout(() => {
                scrollToBottomChatNew(messageContainerRef);
            }, 0);
        }
    } else if (messageHistory.data.length) {
        setChatData((prev) => {
            const seen = new Set(prev.map((row) => Number(row?.id)));
            const older = messageHistory.data.filter((row) => row && !seen.has(Number(row.id)));
            return older.length ? [...prev, ...older] : prev;
        });
    }
    loadingOlderRef.current = false;
    return () => {
        clearTimeout(openTimer);
        clearTimeout(scrollTimer);
    };
}, [messageHistory]);

    useEffect(() => {
        if (!loading) {
            loadingOlderRef.current = false;
        }
    }, [loading]);

    // Socket event handlers
    useEffect(() => {

        const handleDeliverListener = (res) => {
            // const read_res = decryptData(res);
            const read_res = res;
            const deliveredIds = (read_res?.data?.id || []).map(Number);
            if (!read_res?.status || !deliveredIds.length || Number(user_id) === Number(read_res?.data?.other_user_id)) {
                return;
            }
            setChatData((prev) => prev.map((row) => (
                deliveredIds.includes(Number(row?.id)) && Number(row?.is_read) !== 1
                    ? { ...row, is_delivered: 1 }
                    : row
            )));
        };

        const handleReadMessage = (res) => {
            // const read_res = decryptData(res);
            const read_res = res;
            if (!read_res?.status || !read_res?.data?.new_ids?.length) return;

            // Receipt is for the person who sent those messages. Ignore it when
            // this screen is a different user's thread.
            const sentByMe = Number(user_id) === Number(read_res.data.other_user_id);
            const openThread = String(other_user_id) === String(read_res.data.user_id);
            if (!sentByMe || !openThread) return;

            read_res.data.new_ids.forEach((data) => {
                $(`#message${data}`).removeClass('fa-check').addClass('fa-solid fa-check-double class-blue');
            });
            const readIds = read_res.data.new_ids.map(Number);
            setChatData((prev) => prev.map((messageRow) => (
                readIds.includes(Number(messageRow?.id))
                    ? { ...messageRow, is_read: 1, is_delivered: 1 }
                    : messageRow
            )));
        };

        const handleReceiveMessage = (res) => {
            // setProductDetailsStatus(false);
            // console.log("handleReceiveMessage", res);
            // const new_res = decryptData(res);
            const new_res = res;

            if (user_id == new_res?.other_user_id) {
                dispatch(getChatUserAction());
            }

            if (conversationId && (conversationId != new_res?.group_id || !location?.pathname?.includes('message'))) {
                return;
            }
            const viewingThisThread = location?.pathname?.includes('message')
                && String(other_user_id) === String(new_res?.user_id)
                && String(new_res?.user_id) !== String(user_id)
                && conversationId
                && String(conversationId) === String(new_res?.group_id);
            if (viewingThisThread) {
                const read = {
                    other_user_id: new_res?.user_id,
                    ids: [new_res?.id],
                    type: "msg_read"
                };
                // socket.emit('read_message', encryptData(read));
                socket.emit('read_message', read);
                dispatch(getChatUserAction());
            }
           console.log("appendMessageToDOM", new_res);
            
            appendMessageToDOM(new_res);
            scrollToBottomChat();
        };

        const handleMessagesDeleted = (res) => {
            const ids = res?.data?.message_ids || [];
            if (!ids.length) return;
            setChatData((prev) => prev.filter((m) => !ids.map(Number).includes(Number(m.id))));
            setSelectedIds((prev) => prev.filter((id) => !ids.map(Number).includes(Number(id))));
        };

        const handleDisappearUpdated = (res) => {
            const groupId = res?.data?.group_id;
            const duration = Number(res?.data?.duration_seconds) || 0;
            if (groupId) {
                dispatch(updateChatDisappearAction(groupId, duration));
            }
            if (groupId && conversationId && String(groupId) === String(conversationId)) {
                setDisappearSeconds(duration);
            }
        };

        const handleDeleteChatResponse = (res) => {
            if (res?.status) {
                setChatData([]);
                setSelectedIds([]);
                setSelectionMode(false);
                setShowChatMenu(false);
                sessionStorage.removeItem('other_user_id');
                sessionStorage.removeItem('socket_group_id');
                sessionStorage.removeItem('other_user_name');
                sessionStorage.removeItem('other_profile_picture_full_thumb');
                dispatch(getMessageHistoryAction('RESET'));
                dispatch(getChatUserAction());
                alert.success(res.msg || 'Chat deleted');
            } else {
                alert.error(res?.msg || 'Could not delete chat');
            }
        };

        const handleDeleteMessagesResponse = (res) => {
            if (res?.status) {
                const removed = [
                    ...(res.data?.deleted_for_everyone || []),
                    ...(res.data?.deleted_for_me || [])
                ];
                if (removed.length) {
                    setChatData((prev) => prev.filter((m) => !removed.map(Number).includes(Number(m.id))));
                }
                setSelectedIds([]);
                setSelectionMode(false);
                setShowDeleteModal(false);
                if (res.data?.skipped?.length && !removed.length) {
                    alert.error('Those messages cannot be deleted for everyone (not yours or older than 2 days).');
                }
            } else {
                alert.error(res?.msg || 'Could not delete messages');
            }
        };

        // Socket event listeners
        socket?.off('is_deliver_listener')?.on('is_deliver_listener', handleDeliverListener);
        socket?.off('receive_read_message')?.on('receive_read_message', handleReadMessage);
        socket?.off('receive_message')?.on('receive_message', handleReceiveMessage);
        socket?.off('messages_deleted')?.on('messages_deleted', handleMessagesDeleted);
        // Use named off so we don't wipe Users.js listener for the same event
        socket?.off('disappear_settings_updated', handleDisappearUpdated);
        socket?.on('disappear_settings_updated', handleDisappearUpdated);
        socket?.off('delete_chat_response')?.on('delete_chat_response', handleDeleteChatResponse);
        socket?.off('delete_messages_response')?.on('delete_messages_response', handleDeleteMessagesResponse);

        return () => {
            socket?.off('is_deliver_listener', handleDeliverListener);
            socket?.off('receive_read_message', handleReadMessage);
            socket?.off('receive_message', handleReceiveMessage);
            socket?.off('messages_deleted', handleMessagesDeleted);
            socket?.off('disappear_settings_updated', handleDisappearUpdated);
            socket?.off('delete_chat_response', handleDeleteChatResponse);
            socket?.off('delete_messages_response', handleDeleteMessagesResponse);
        };
    }, [user_id, other_user_id, conversationId, location?.pathname, dispatch, alert]);

    // Product details effect
    // useEffect(() => {
    //     const productDetails = JSON.parse(sessionStorage.getItem('productDetails'));
    //     setTimeout(() => {
    //         if (productDetails) {
    //             const attachment = {
    //                 product_url: productDetails?.slug + '/' + productDetails?.id,
    //                 image_url: productDetails?.image
    //             };
    //             const { carat, certificate, clarity, color, shape, seller_id, stockNumber, cut, polish, symmetry, fluorescence } = productDetails;
    //             const message = `${shape} ${carat}ct  ${color ? color : ''} ${clarity ? clarity : ''} ${certificate ? certificate : ''} ${cut ? cut : ''} ${polish ? polish : ''} ${symmetry ? symmetry : ''} ${fluorescence ? fluorescence : ''} ${stockNumber ? stockNumber : ''}`;

    //             const data = {
    //                 product_id: productDetails?.id,
    //                 slug: productDetails?.slug,
    //                 other_user_id: seller_id,
    //                 message: message,
    //                 attachment_url: JSON.stringify(attachment),
    //                 type: 'ENQUIRY'
    //             };
    //             // socket.emit('send_message', encryptData(data));
    //             socket.emit('send_message', data);
    //             // socket?.emit('total_message_count', encryptData({ user_id: other_user_id }));
    //             socket?.emit('total_message_count', { user_id: other_user_id });
    //             socket.emit('total_message_count', {});
    //             setProductDetailsStatus(true);
    //             scrollToBottomChat();
    //         }
    //     }, 2000);
    // }, []);

    // useEffect(() => {
    //     if (socket?.connected) {
    //         window.onload = () => {
    //             setTimeout(() => {
    //                 sessionStorage.removeItem('productDetails');
    //             }, 2500);
    //         };
    //     }
    // }, [socket?.connected]);

    useEffect(() => {
        if (!socket?.connected) return;

        const params = new URLSearchParams(window.location.search);
        const chatId = params.get('chat');

        if (!chatId) {
            sessionStorage.removeItem('productDetails');
        }
    }, [socket?.connected]);


    const appendMessageToDOM = useCallback((new_res) => {
        if (!new_res) return;

        const socketCreatedAt = sessionStorage.getItem('socket_created_at');
        const socketGroupId = sessionStorage.getItem('socket_group_id');
        const socketMessage = sessionStorage.getItem('socket_message');
        const sameSocketMessage = socketCreatedAt &&
            socketCreatedAt === new_res?.created_at &&
            socketGroupId === new_res?.group_id &&
            socketMessage === new_res?.message;
        if (sameSocketMessage) return;

        sessionStorage.setItem('socket_created_at', new_res?.created_at || '');
        sessionStorage.setItem('socket_group_id', new_res?.group_id || '');
        sessionStorage.setItem('socket_message', new_res?.message || '');
        sessionStorage.setItem('socket_flag', 1);

        setChatData((prev) => {
            if (prev.some((row) => Number(row?.id) === Number(new_res.id))) return prev;
            return [new_res, ...prev];
        });
        setTimeout(() => scrollToBottomChatNew(messageContainerRef), 0);
    }, []);

    const handleSendMessage = useCallback(() => {
        if (!message || message.trim().length <= 0) {
            alert.error('Message is required');
            return;
        }

        if (socket?.connected === false) {
            socket.connected = true;
        }



        const productDetails = JSON.parse(sessionStorage.getItem('productDetails'));
           
            
            if (productDetails && !enquirySentRef.current && other_user_id == productDetails?.seller_id) {
                 console.log('productDetails other_user_id', other_user_id, 'productDetails seller_id', productDetails?.seller_id );
                //  setTimeout(() => {   
                enquirySentRef.current = true;
                const attachment = {
                    product_url: productDetails?.slug + '/' + productDetails?.id,
                    image_url: productDetails?.image
                };
                const { carat, certificate, clarity, color, shape, seller_id, stockNumber, cut, polish, symmetry, fluorescence } = productDetails;
                const message = `${shape} ${carat}ct  ${color ? color : ''} ${clarity ? clarity : ''} ${certificate ? certificate : ''} ${cut ? cut : ''} ${polish ? polish : ''} ${symmetry ? symmetry : ''} ${fluorescence ? fluorescence : ''} ${stockNumber ? stockNumber : ''}`;
                
                const enquiryData = {
                    product_id: productDetails?.id,
                    slug: productDetails?.slug,
                    other_user_id: seller_id,
                    message: message,
                    attachment_url: JSON.stringify(attachment),
                    type: 'ENQUIRY'
                };
                console.log('product details enq', enquiryData);
                
                // socket.emit('send_message', encryptData(data));
                socket.emit('send_message', enquiryData);
                // socket?.emit('total_message_count', encryptData({ user_id: other_user_id }));
                socket?.emit('total_message_count', { user_id: other_user_id });
                socket.emit('total_message_count', {});
                sessionStorage.removeItem('productDetails');
                setProductDetailsStatus(true);
                // scrollToBottomChat();
                // }, 700);
            }

        const data = {
            other_user_id: other_user_id,
            message: message,
            type: 'TEXT'
        };

        // socket?.emit('send_message', encryptData(data));
        socket?.emit('send_message', data);
        setMessage('');
        // socket?.emit('total_message_count', encryptData({ user_id: other_user_id }));
        socket?.emit('total_message_count', { user_id: other_user_id });
        socket?.emit('total_message_count', {});
        scrollToBottomChat();

                  
        
        



    }, [message, other_user_id, alert]);

    const handleSendMessageKeyPress = useCallback((e) => {
        if (e.key === 'Enter') {
            handleSendMessage();
        }
    }, [handleSendMessage]);

    const handleButtonClick = useCallback((msg) => {
        if (socket?.connected === false) {
            socket.connected = true;
        }


        const productDetails = JSON.parse(sessionStorage.getItem('productDetails'));
           
            
            if (productDetails && !enquirySentRef.current && other_user_id == productDetails?.seller_id) {
                 console.log('productDetails other_user_id', other_user_id, 'productDetails seller_id', productDetails?.seller_id );
                //  setTimeout(() => {   
                enquirySentRef.current = true;
                const attachment = {
                    product_url: productDetails?.slug + '/' + productDetails?.id,
                    image_url: productDetails?.image
                };
                const { carat, certificate, clarity, color, shape, seller_id, stockNumber, cut, polish, symmetry, fluorescence } = productDetails;
                const message = `${shape} ${carat}ct  ${color ? color : ''} ${clarity ? clarity : ''} ${certificate ? certificate : ''} ${cut ? cut : ''} ${polish ? polish : ''} ${symmetry ? symmetry : ''} ${fluorescence ? fluorescence : ''} ${stockNumber ? stockNumber : ''}`;
                
                const enquiryData = {
                    product_id: productDetails?.id,
                    slug: productDetails?.slug,
                    other_user_id: seller_id,
                    message: message,
                    attachment_url: JSON.stringify(attachment),
                    type: 'ENQUIRY'
                };
                console.log('product details enq', enquiryData);
                
                // socket.emit('send_message', encryptData(data));
                socket.emit('send_message', enquiryData);
                // socket?.emit('total_message_count', encryptData({ user_id: other_user_id }));
                socket?.emit('total_message_count', { user_id: other_user_id });
                socket.emit('total_message_count', {});
                sessionStorage.removeItem('productDetails');
                setProductDetailsStatus(true);
                // scrollToBottomChat();
                // }, 700);
            }

        const data = {
            other_user_id: other_user_id,
            message: msg,
            type: 'TEXT'
        };

        // socket?.emit('send_message', encryptData(data));
        socket?.emit('send_message', data);
        // socket?.emit('total_message_count', encryptData({ user_id: other_user_id }));
        socket?.emit('total_message_count', { user_id: other_user_id });
        socket?.emit('total_message_count', {});
        scrollToBottomChat();
    }, [other_user_id]);

    const handleImage = useCallback(() => {
        $('.s-i-image').css('display', 'block');
    }, []);

    const toggleSelectMessage = useCallback((id) => {
        if (!id) return;
        setSelectionMode(true);
        setSelectedIds((prev) => {
            const num = Number(id);
            if (prev.map(Number).includes(num)) {
                return prev.filter((x) => Number(x) !== num);
            }
            return [...prev, num];
        });
    }, []);

    const clearSelection = useCallback(() => {
        setSelectedIds([]);
        setSelectionMode(false);
        setShowDeleteModal(false);
    }, []);

    const selectedMessages = useMemo(
        () => chatData.filter((m) => selectedIds.map(Number).includes(Number(m.id))),
        [chatData, selectedIds]
    );

    const canDeleteForEveryone = useMemo(
        () => selectedMessages.length > 0 && selectedMessages.every((m) => canDeleteMessageForEveryone(m, user_id)),
        [selectedMessages, user_id]
    );

    const handleDeleteEntireChat = useCallback(() => {
        if (other_user_id === '-1') return;
        if (!window.confirm('Delete this chat from your account? The other person will still see it.')) {
            return;
        }
        setShowChatMenu(false);
        socket.emit('delete_chat', {
            group_id: conversationId,
            other_user_id: other_user_id
        });
    }, [conversationId, other_user_id]);

    const emitDeleteMessages = useCallback((mode) => {
        if (!selectedIds.length) return;
        socket.emit('delete_messages', {
            message_ids: selectedIds.map(Number),
            mode,
            other_user_id
        });
    }, [selectedIds, other_user_id]);

    const setChatDisappear = useCallback((duration) => {
        socket.emit('set_disappear_chat', {
            group_id: conversationId,
            other_user_id,
            duration_seconds: duration
        });
        setDisappearSeconds(duration);
        if (conversationId) {
            dispatch(updateChatDisappearAction(conversationId, duration));
        }
        setShowChatMenu(false);
    }, [conversationId, other_user_id, dispatch]);

    const disappearLabel = useMemo(() => {
        if (Number(disappearSeconds) === DURATION_7D) return '7 days';
        if (Number(disappearSeconds) === DURATION_24H) return '24 hours';
        return 'Off';
    }, [disappearSeconds]);




    const handleScroll = useCallback(() => {
        if (hash) {
            const container = messageContainerRef.current;
            if (!container) return;
            if ((container.scrollTop <= 200) && (!loading) && !loadingOlderRef.current && (messageHistory?.total_record > chatData?.length) && (hash == '1')) {
                const oldScrollHeight = container.scrollHeight;
                loadingOlderRef.current = true;
                setPage(prev => prev + 1);
                dispatch(getMessageHistoryAction(user_id, Number(other_user_id), page + 1, limit))
                    .then(() => {
                        const newScrollHeight = container.scrollHeight;
                        container.scrollTop = newScrollHeight - oldScrollHeight + 210 + container.scrollTop;
                    });
            }
        }
    }, [hash, loading, messageHistory?.total_record, limit, page, user_id, other_user_id]);


    // ============================================================================
    // MEMOIZED VALUES
    // ============================================================================

    const reversedChatData = useMemo(() => chatData?.slice(0)?.reverse(), [chatData]);

    useEffect(() => {
        const last = reversedChatData?.[reversedChatData.length - 1];
        lastChatDayRef.current = last?.created_at ? chatCalendarDay(last.created_at) : '';
    }, [reversedChatData, other_user_id]);
    const isCevaheer = other_user_id === '-1';
    const hasThread = Boolean(other_user_id);
    // Loader only while the first page of history is in flight. An empty thread
    // (deleted chat or disappeared messages) is not a loading state.
    const showInitialLoader = hasThread && Boolean(loading) && (chatData?.length ?? 0) === 0;
    const showWelcome = !hasThread;

    useEffect(() => {
        const selected = sessionStorage.getItem('other_user_id');
        if (!selected || selected === '-1' || !Array.isArray(chatuser)) return;

        const stillListed = chatuser.some((user) => {
            const rowId = user.user_id == user_id ? user.other_user_id : user.user_id;
            return String(rowId) === String(selected);
        });
        if (stillListed) return;

        sessionStorage.removeItem('other_user_id');
        sessionStorage.removeItem('socket_group_id');
        sessionStorage.removeItem('other_user_name');
        sessionStorage.removeItem('other_profile_picture_full_thumb');
        setChatData([]);
        setSelectedIds([]);
        setSelectionMode(false);
        dispatch(getMessageHistoryAction('RESET'));
    }, [chatuser, user_id, dispatch]);

    useEffect(() => {
        const container = messageContainerRef.current;

        if (container) {
            if (showInitialLoader) {
                container.classList.add('scroll-disabled');
            } else {
                container.classList.remove('scroll-disabled');
            }
        }
    }, [showInitialLoader]);



    return (
        <Fragment>
            {showWelcome ? (
                <Welcome />
            ) : (
                <Fragment>
                    <div className="col-md-7 col-lg-9">
                        <div className="msgdetail">
                            <div className="chat-thread-header">
                                <div className="chat-thread-user">
                                    <Avatar
                                        name={other_user_name}
                                        src={other_profile_picture_full_thumb}
                                        color="#012F5B"
                                        size="45"
                                        value="86%"
                                        round
                                    />
                                    <div className="chat-thread-user-meta">
                                        <h3>
                                            {isCevaheer ? 'Cevaheer' : other_user_name}
                                            {disappearSeconds > 0 ? (
                                                <span
                                                    className="chat-disappear-icon"
                                                    title={`Disappearing messages: ${disappearLabel}`}
                                                    aria-label="Auto-disappear active"
                                                >
                                                    <i className="fa fa-clock-o" aria-hidden="true"></i>
                                                </span>
                                            ) : null}
                                        </h3>
                                        {disappearSeconds > 0 ? (
                                            <span className="chat-disappear-status">Disappear: {disappearLabel}</span>
                                        ) : null}
                                    </div>
                                </div>
                                {other_user_id !== '-1' ? (
                                    <div className="chat-header-actions">
                                        {selectionMode ? (
                                            <div className="chat-selection-bar">
                                                <span>{selectedIds.length} selected</span>
                                                <button
                                                    type="button"
                                                    className="chat-icon-btn"
                                                    disabled={!selectedIds.length}
                                                    onClick={() => setShowDeleteModal(true)}
                                                    title="Delete"
                                                >
                                                    <i className="fa fa-trash" aria-hidden="true"></i>
                                                </button>
                                                <button type="button" className="chat-selection-cancel" onClick={clearSelection}>
                                                    Cancel
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="chat-menu-wrap dropdown" ref={chatMenuRef}>
                                                <button
                                                    type="button"
                                                    className={`chat-icon-btn chat-menu-toggle${showChatMenu ? ' open' : ''}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setShowChatMenu((v) => !v);
                                                    }}
                                                    aria-label="Chat options"
                                                    aria-expanded={showChatMenu}
                                                >
                                                    <i className="fa fa-ellipsis-v" aria-hidden="true"></i>
                                                </button>
                                                {showChatMenu ? (
                                                    <div className="dropdown-menu dropdown-menu-right show chat-thread-dropdown">
                                                        <button
                                                            type="button"
                                                            className="dropdown-item"
                                                            onClick={() => {
                                                                setShowChatMenu(false);
                                                                setSelectionMode(true);
                                                            }}
                                                        >
                                                            Select messages
                                                        </button>
                                                        <div className="dropdown-divider"></div>
                                                        <div className="chat-dd-section-label">
                                                            Auto-disappear
                                                            <span className="chat-dd-current">{disappearLabel}</span>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className={`dropdown-item chat-dd-option${Number(disappearSeconds) === 0 ? ' active' : ''}`}
                                                            onClick={() => setChatDisappear(0)}
                                                        >
                                                            <span>Off</span>
                                                            {Number(disappearSeconds) === 0 ? <i className="fa fa-check" aria-hidden="true"></i> : null}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`dropdown-item chat-dd-option${Number(disappearSeconds) === DURATION_24H ? ' active' : ''}`}
                                                            onClick={() => setChatDisappear(DURATION_24H)}
                                                        >
                                                            <span>24 hours</span>
                                                            {Number(disappearSeconds) === DURATION_24H ? <i className="fa fa-check" aria-hidden="true"></i> : null}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`dropdown-item chat-dd-option${Number(disappearSeconds) === DURATION_7D ? ' active' : ''}`}
                                                            onClick={() => setChatDisappear(DURATION_7D)}
                                                        >
                                                            <span>7 days</span>
                                                            {Number(disappearSeconds) === DURATION_7D ? <i className="fa fa-check" aria-hidden="true"></i> : null}
                                                        </button>
                                                        <div className="dropdown-divider"></div>
                                                        <button
                                                            type="button"
                                                            className="dropdown-item text-danger"
                                                            onClick={handleDeleteEntireChat}
                                                        >
                                                            Delete entire chat
                                                        </button>
                                                    </div>
                                                ) : null}
                                            </div>
                                        )}
                                    </div>
                                ) : null}
                            </div>
                            {showInitialLoader ? <ChatLoader /> : null}
                            <div
                                className="message_list"
                                ref={messageContainerRef}
                                onScroll={handleScroll}
                                id='scrollbar'
                            >

                                {reversedChatData?.map((messageData, key) => {
                                    const previous = key > 0 ? reversedChatData[key - 1] : null;
                                    const day = chatCalendarDay(messageData?.created_at);
                                    const showDate = Boolean(day) && day !== chatCalendarDay(previous?.created_at);
                                    return (
                                        <Fragment key={messageData?.id || key}>
                                            {showDate ? (
                                                <div className="chat-date-divider">
                                                    <span>{formatChatDayLabel(messageData.created_at)}</span>
                                                </div>
                                            ) : null}
                                            <MessageItem
                                                messageData={messageData}
                                                user_id={user_id}
                                                handleImage={handleImage}
                                                isOwnMessage={user_id === messageData.user_id}
                                                selectionMode={selectionMode}
                                                selected={selectedIds.map(Number).includes(Number(messageData?.id))}
                                                onToggleSelect={toggleSelectMessage}
                                            />
                                        </Fragment>
                                    );
                                })}
                            </div>
                            {other_user_id !== '-1' && (
                                <div className="row mt-5 pt-4 brdr-top msgFooter">
                                    <div className="col-md-12 pl-md-0">
                                        <div className='chat-suggestion'>
                                            {Array.isArray(shortmsg) && shortmsg.length > 0 ? (
                                                shortmsg.map((msg, index) => (
                                                    <button
                                                        className="btn cs-btn"
                                                        key={index}
                                                        onClick={() => handleButtonClick(msg)}
                                                    >
                                                        {msg}
                                                    </button>
                                                ))
                                            ) : (
                                                <p></p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-md-9 pl-md-0">
                                        <input
                                            type="text"
                                            placeholder={localStorage.getItem('lang') === 'tr' ? 'Mesaj yazın...' : 'Write a message...'}
                                            className="form-control"
                                            value={message}
                                            onChange={(e) => setMessage(e.target.value)}
                                            onKeyDown={handleSendMessageKeyPress}
                                        />
                                    </div>
                                    <div className="col-md-3 pl-md-0 pr-md-0">
                                        <button className="btn-black" onClick={handleSendMessage}>
                                            <FormattedMessage id="Send" defaultMessage="Send" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <DeleteMessageModal
                        open={showDeleteModal}
                        count={selectedIds.length}
                        canDeleteForEveryone={canDeleteForEveryone}
                        onEveryone={() => emitDeleteMessages('everyone')}
                        onOnlyMe={() => emitDeleteMessages('me')}
                        onCancel={() => setShowDeleteModal(false)}
                    />
                </Fragment>
            )}
        </Fragment>
    );
};

export default React.memo(Chatbox); 